// Realtime two-way sync (web). Opens a WebSocket to the shared backend using the
// browser's auth cookie. When ANOTHER client (e.g. a tech on mobile) changes
// data, we fire a window event; screens subscribe via useLiveRefresh() and
// re-pull. Our own writes are ignored (matched by CLIENT_ID).
import { useEffect, useRef } from "react";
import { api, CLIENT_ID } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EVENT = "amscale:sync";
const WS_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");

let socket = null;
let retry = 0;
let started = false;
let timer = null;

function scheduleReconnect() {
  retry = Math.min(retry + 1, 6);
  clearTimeout(timer);
  timer = setTimeout(connect, Math.min(1000 * 2 ** retry, 15000));
}

function connect() {
  if (!started || !WS_BASE) return;
  try {
    socket = new WebSocket(`${WS_BASE}/api/ws`);
  } catch {
    scheduleReconnect();
    return;
  }
  socket.onopen = () => {
    retry = 0;
  };
  socket.onmessage = (ev) => {
    try {
      const d = JSON.parse(ev.data);
      if (d?.type === "change" && d.origin !== CLIENT_ID) {
        window.dispatchEvent(new CustomEvent(EVENT, { detail: d }));
      }
    } catch {
      /* ignore */
    }
  };
  socket.onclose = async () => {
    if (!started) return;
    // First drop may be an expired access-token cookie — refresh then reconnect.
    if (retry === 0) {
      try {
        await api.post("/auth/refresh");
      } catch {
        /* ignore */
      }
    }
    scheduleReconnect();
  };
  socket.onerror = () => {
    try {
      socket.close();
    } catch {
      /* ignore */
    }
  };
}

export function startRealtime() {
  if (started) return;
  started = true;
  retry = 0;
  connect();
}

export function stopRealtime() {
  started = false;
  clearTimeout(timer);
  if (socket) {
    try {
      socket.close();
    } catch {
      /* ignore */
    }
    socket = null;
  }
}

// Mount once inside AuthProvider; connects while signed in.
export function RealtimeMount() {
  const { user } = useAuth();
  useEffect(() => {
    if (!user) return undefined;
    startRealtime();
    return () => stopRealtime();
  }, [user]);
  return null;
}

// Subscribe a screen's refetch to remote changes (debounced).
export function useLiveRefresh(fn) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    let t = null;
    const handler = () => {
      clearTimeout(t);
      t = setTimeout(() => ref.current && ref.current(), 350);
    };
    window.addEventListener(EVENT, handler);
    return () => {
      clearTimeout(t);
      window.removeEventListener(EVENT, handler);
    };
  }, []);
}

// Subscribe to the raw change event (gives {path, method, origin, ts}) — used
// for targeted UI reactions like flashing a "new photo" badge.
export function useLiveEvent(fn) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const handler = (e) => ref.current && ref.current(e.detail || {});
    window.addEventListener(EVENT, handler);
    return () => window.removeEventListener(EVENT, handler);
  }, []);
}
